<template>
  <div>
    <loading
      :active.sync="loading"
      :can-cancel="false"
      background-color="#fff"
      :is-full-page="false"
    ></loading>

    <div v-for="error_message in errors" v-if="errors.length > 0">
      <CAlert color="danger" closeButton>{{ error_message }}</CAlert>
    </div>

    <!-- Filters -->
    <section>
      <CCard>
        <CCardHeader>
          <strong>Filtros</strong>
          <div class="card-header-actions">
            <b-button @click="fetchData()">Consultar</b-button>
          </div>
        </CCardHeader>
        <CCardBody>
          <b-row>
            <b-col lg="3" offset-lg="3" sm="12">
              <b-form-group>
                <small>Fechas</small>
                <v-date-picker mode="range" v-model="selectedDate" />
              </b-form-group>
            </b-col>
            <b-col lg="3" sm="12">
              <b-form-group>
                <small>Ciudades</small>
                <v-select
                  v-model="citySelected"
                  :options="cities"
                  placeholder="Todas las ciudades"
                >
                </v-select>
              </b-form-group>
            </b-col>
          </b-row>
        </CCardBody>
      </CCard>
    </section>

    <!-- KPI Cards -->
    <section v-if="summary">
      <b-row class="mb-3">
        <b-col lg="4" sm="12">
          <CCard class="text-center">
            <CCardBody>
              <div class="text-muted small mb-1">Chats del período</div>
              <h2 class="mb-0 font-weight-bold text-primary">{{ summary.totalChats }}</h2>
            </CCardBody>
          </CCard>
        </b-col>
        <b-col lg="4" sm="12">
          <CCard class="text-center">
            <CCardBody>
              <div class="text-muted small mb-1">Clientes registrados (bot)</div>
              <h2 class="mb-0 font-weight-bold text-success">{{ summary.totalRegistered }}</h2>
            </CCardBody>
          </CCard>
        </b-col>
        <b-col lg="4" sm="12">
          <CCard class="text-center">
            <CCardBody>
              <div class="text-muted small mb-1">Pagos registrados</div>
              <h2 class="mb-0 font-weight-bold text-warning">{{ summary.totalWithSale }}</h2>
              <small class="text-muted">clientes únicos que pagaron</small>
            </CCardBody>
          </CCard>
        </b-col>
      </b-row>
    </section>

    <!-- Charts -->
    <section>
      <b-row>
        <b-col lg="6" style="text-align: center">
          <CCard>
            <CCardHeader>
              <CButton
                color="primary"
                variant="ghost"
                v-c-popover="{
                  header: 'Leyenda',
                  content: 'Total de chats WhatsApp nuevos por día en el período (fuente: BD ai-agent-backend)'
                }"
              >
                Chats por día
              </CButton>
            </CCardHeader>
            <CCardBody>
              <CChartBar
                v-if="datasetsMessages && labelsMessages"
                :datasets="datasetsMessages"
                :labels="labelsMessages"
                :options="chartOptions"
              />
            </CCardBody>
          </CCard>
        </b-col>

        <b-col lg="6" style="text-align: center">
          <CCard>
            <CCardHeader>
              <CButton
                color="primary"
                variant="ghost"
                v-c-popover="{
                  header: 'Leyenda',
                  content: 'Clientes por estado del embudo comercial (sell_state), actualizados en el período',
                  placement: 'left'
                }"
              >
                Detalle de prospectos
              </CButton>
            </CCardHeader>
            <CCardBody>
              <CChartBar
                v-if="datasetsSellState && labelsSellState"
                :datasets="datasetsSellState"
                :labels="labelsSellState"
                :options="chartOptions"
              />
            </CCardBody>
          </CCard>
        </b-col>

        <b-col lg="6" style="text-align: center">
          <CCard>
            <CCardHeader>
              <CButton
                color="primary"
                variant="ghost"
                v-c-popover="{
                  header: 'Leyenda',
                  content: 'Clientes registrados por el bot (registered_provider = true) por día'
                }"
              >
                Clientes registrados en plataforma
              </CButton>
            </CCardHeader>
            <CCardBody>
              <CChartBar
                v-if="datasetsRegistered && labelsRegistered"
                :datasets="datasetsRegistered"
                :labels="labelsRegistered"
                :options="chartOptions"
              />
            </CCardBody>
          </CCard>
        </b-col>

        <b-col lg="6" style="text-align: center">
          <CCard>
            <CCardHeader>
              <CButton
                color="primary"
                variant="ghost"
                v-c-popover="{
                  header: 'Leyenda',
                  content: 'Pagos registrados por día (fuente: Applikalo tabla 17635). Clientes únicos que realizaron al menos un pago.',
                  placement: 'left'
                }"
              >
                Pagos del período (ventas reales)
              </CButton>
            </CCardHeader>
            <CCardBody>
              <CChartBar
                v-if="datasetsPayments && labelsPayments"
                :datasets="datasetsPayments"
                :labels="labelsPayments"
                :options="chartOptions"
              />
            </CCardBody>
          </CCard>
        </b-col>
      </b-row>
    </section>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import { CChartBar, CChartLine } from "@coreui/vue-chartjs";
import { DatePicker } from "v-calendar";
import moment from "moment";
import axios from "axios";
moment.locale("es");

const today = new Date();
const AI_AGENT_URL = process.env.VUE_APP_AI_AGENT_URL || "https://us-central1-ai-agent-365.cloudfunctions.net";
// Clientes y pagos se piden a la function `kpis` de goguauendpoints, que
// consulta Applikalo desde el servidor. Applikalo no responde al preflight
// CORS, así que llamarlo directo desde el navegador falla; además los tokens
// no pueden vivir en el bundle público.
const GOGUAU_CITY_IDS = "1,39,105,106"; // Popayán=1, Pereira=39, Ibagué=105, Armenia=106

const chartBarOptions = {
  responsive: true,
  legend: { position: "bottom" },
  scales: { yAxes: [{ ticks: { beginAtZero: true } }] },
  tooltips: { enabled: true },
  animation: {
    duration: 500,
    easing: "easeOutQuart",
    onComplete: function() {
      var ctx = this.chart.ctx;
      ctx.font = Chart.helpers.fontString(
        Chart.defaults.global.defaultFontFamily,
        "normal",
        Chart.defaults.global.defaultFontFamily
      );
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      this.data.datasets.forEach(function(dataset) {
        for (var i = 0; i < dataset.data.length; i++) {
          var model = dataset._meta[Object.keys(dataset._meta)[0]].data[i]._model;
          var scale_max = dataset._meta[Object.keys(dataset._meta)[0]].data[i]._yScale.maxHeight;
          ctx.fillStyle = "#444";
          var y_pos = model.y - 5;
          if ((scale_max - model.y) / scale_max >= 0.93) y_pos = model.y + 20;
          ctx.fillText(dataset.data[i], model.x, y_pos);
        }
      });
    }
  }
};

export default {
  name: "adminClients",
  components: {
    Loading,
    CChartBar,
    CChartLine,
    "v-date-picker": DatePicker
  },
  data() {
    return {
      loading: true,
      errors: [],
      selectedDate: {
        start: new Date(today.getFullYear(), today.getMonth(), 1),
        end: new Date(today.getFullYear(), today.getMonth(), 28)
      },
      cities: [{ value: 0, label: "Todas" }],
      citySelected: { value: 0, label: "Todas" },
      chartOptions: chartBarOptions,
      summary: null,
      // Mensajes por día
      datasetsMessages: null,
      labelsMessages: null,
      // Clientes registrados por día
      datasetsRegistered: null,
      labelsRegistered: null,
      // Estados de venta
      datasetsSellState: null,
      labelsSellState: null,
      // Pagos (Applikalo 17635)
      datasetsPayments: null,
      labelsPayments: null
    };
  },
  created() {
    this.userProfile = JSON.parse(this.$store.getters.userProfile);
    if (this.userProfile.status.id == 2) {
      this.fetchCities();
    } else {
      this.errors.push("No tienes acceso a este módulo");
      this.loading = false;
    }
  },
  methods: {
    fetchCities() {
      let getCities = this.$firebase.functions().httpsCallable("getCitiesV2");
      getCities()
        .then(response => {
          if (response.data != null) {
            let cities = response.data.data;
            cities.forEach(city => {
              this.cities.push({ value: city.id, label: city.nombre });
            });
          }
          this.fetchData();
        })
        .catch(error => {
          console.error("fetchCities error:", error);
          this.fetchData();
        });
    },

    fetchData() {
      this.loading = true;
      this.errors = [];
      this.summary = { totalChats: 0, totalRegistered: 0, totalWithSale: 0 };
      const dateStart = moment(this.selectedDate.start).format("YYYY-MM-DD");
      const dateEnd = moment(this.selectedDate.end).format("YYYY-MM-DD");

      Promise.all([
        this.fetchConversationKpis(dateStart, dateEnd),
        this.fetchRegisteredClients(dateStart, dateEnd),
        this.fetchPayments(dateStart, dateEnd)
      ])
        .catch(err => {
          console.error("fetchData error:", err);
          this.errors.push("Error al cargar los datos. Intenta de nuevo.");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async fetchConversationKpis(dateStart, dateEnd) {
      const response = await axios.post(`${AI_AGENT_URL}/getConversationKpis`, {
        branchIds: [9, 10, 11, 12],
        dateStart,
        dateEnd
      });
      const data = response.data;

      this.summary.totalChats = data.summary ? data.summary.totalChats : 0;

      // Chats por día — normalizar a minúsculas para evitar mismatch de locale
      const chatMap = {};
      (data.chatsPerDay || []).forEach(d => {
        chatMap[moment(d.date).locale("en").format("DD MMM").toLowerCase()] = d.count;
      });
      this.labelsMessages = (data.labels || []).map(l => l.toLowerCase());
      this.datasetsMessages = [{
        label: "Chats",
        backgroundColor: "rgba(54, 162, 235, 0.6)",
        borderColor: "rgba(54, 162, 235, 1)",
        data: this.labelsMessages.map(l => chatMap[l] || 0)
      }];

      // Sell states
      const states = data.clientsBySellState || [];
      this.labelsSellState = states.map(s => s.stateName);
      this.datasetsSellState = [{
        label: "Clientes por estado",
        backgroundColor: states.map(s => s.color || "#666"),
        data: states.map(s => s.count)
      }];
    },

    async fetchRegisteredClients(dateStart, dateEnd) {
      const kpis = this.$firebase.functions().httpsCallable("kpis");
      const response = await kpis({
        route: "registeredClients",
        dateStart,
        dateEnd,
        cityIds: GOGUAU_CITY_IDS
      });

      const clientes = (response.data && response.data.data) || [];
      this.summary.totalRegistered = clientes.length;

      // Clientes registrados por día
      const regMap = {};
      clientes.forEach(c => {
        const day = moment(c.fecha_creacion).format("DD MMM");
        regMap[day] = (regMap[day] || 0) + 1;
      });

      const current = moment(this.selectedDate.start).clone().startOf("day");
      const endDate = moment(this.selectedDate.end);
      const regLabels = [];
      while (current.isSameOrBefore(endDate, "day")) {
        regLabels.push(current.format("DD MMM"));
        current.add(1, "day");
      }

      this.labelsRegistered = regLabels;
      this.datasetsRegistered = [{
        label: "Clientes registrados",
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        data: regLabels.map(l => regMap[l] || 0)
      }];
    },

    async fetchPayments(dateStart, dateEnd) {
      const kpis = this.$firebase.functions().httpsCallable("kpis");
      const response = await kpis({
        route: "payments",
        dateStart,
        dateEnd,
        cityIds: GOGUAU_CITY_IDS
      });

      const pagos = (response.data && response.data.data) || [];

      // Clientes únicos con venta
      const uniqueClients = new Set(pagos.map(p => p.cliente && p.cliente.id).filter(Boolean));
      this.summary.totalWithSale = uniqueClients.size;

      // Pagos por día
      const payMap = {};
      pagos.forEach(p => {
        const day = moment(p.fecha_creacion).format("DD MMM");
        payMap[day] = (payMap[day] || 0) + 1;
      });

      // Usar las mismas labels del período
      const dateStart_ = moment(this.selectedDate.start);
      const dateEnd_ = moment(this.selectedDate.end);
      const payLabels = [];
      const current = dateStart_.clone().startOf("day");
      while (current.isSameOrBefore(dateEnd_, "day")) {
        payLabels.push(current.format("DD MMM"));
        current.add(1, "day");
      }

      this.labelsPayments = payLabels;
      this.datasetsPayments = [{
        label: "Pagos",
        backgroundColor: "rgba(255, 193, 7, 0.6)",
        borderColor: "rgba(255, 193, 7, 1)",
        data: payLabels.map(l => payMap[l] || 0)
      }];
    }
  }
};
</script>
